/**
 * One service — its plans and fees, its document checklist, and the way into
 * its workflow (FR-C1, FR-C2, ADR-0009).
 *
 * The SAC field carries a sentence rather than a placeholder. No shipped
 * template fills it in, and the reason is worth the firm knowing: a SAC code
 * is a tax classification that will ride onto their own invoices under their
 * own GSTIN, so ProDesk does not guess one.
 *
 * **Wave 15F** (C14 §10.6 item 3, ADR-0033): the service is a **record** — one
 * header with what it is for, whether it repeats, its plans and its live
 * workflow version, and *Open the workflow* as the one primary action; its
 * parts are section cards. What each part saves, and when, is unchanged: the
 * details save as each field is left, a fee is set in a dialog (editing one
 * item is a dialog), a plan is added in one, and the checklist saves on its
 * own button.
 *
 * **C23 (Wave 25D, T-4.114, ADR-0045 §4–§5):** the icon the website's services
 * section draws beside this service, picked from the curated set
 * (`IconPicker`, over `/services/vocabulary`'s `icons`). It saves the moment
 * it is picked, like the switches below it; *No icon* saves `null`. Nothing
 * sets one for the firm.
 *
 * **C25 (Wave 27D, T-1.31, ADR-0054 §1–§5):** the record carries its state —
 * active, inactive or deleted — as a badge, with *Deactivate* / *Activate* and
 * *Delete* / *Restore* behind *More* (`moves.tsx`: delete names what still uses
 * it; a restore comes back inactive). The details gain the **description**, the
 * **category** and the **order**. The plans are `ServicePlans.tsx`, split out
 * when this file passed 500 lines: each edited whole in `PlanDialog` (name,
 * description, order, fee, *Show on our website*), each with its own state and
 * badges, and the deleted ones waiting under *Deleted plans* to be restored.
 * An active service whose category is paused is said to be **not
 * offered**, by the service's own answer (`offered`), not this screen's.
 *
 * **C27 (ADR-0056 §4):** *Reverse charge applies by default* — the firm's own
 * default for whether an invoice priced from this service starts with *Tax
 * payable on reverse charge* on. It saves when flipped, like the two switches
 * beside it, and changes no invoice already raised. ProDesk ships no list of
 * which services fall under reverse charge (spec §9-100).
 */

import { Alert, Button, Checkbox, Field, Input, Select, Switch, Textarea, buttonClass } from '@fiveox/ui'
import { FileText, ListChecks, Plus, Settings2, Trash2, Workflow as WorkflowIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router'

import { IconPicker } from '../../components/IconPicker'
import { RecordPage, RecordSection } from '../../components/page'
import { SERVICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type Checklist,
  type ChecklistItem,
  type Service,
  type ServiceCategory,
  type Vocabulary,
  type WorkflowSummary,
  appliesToLabel,
  services,
} from '../../lib/api/services'
import { ImageField } from '../Site/fields/ImageField'
import { orderValue } from './CategoryDialog'
import { type MoveAction, actionsFor, useCatalogueMoves } from './moves'
import { ServicePlans } from './ServicePlans'

const MOVE_LABEL: Record<MoveAction, string> = {
  deactivate: 'Deactivate this service',
  activate: 'Activate this service',
  delete: 'Delete this service',
  restore: 'Restore this service',
}

export function ServiceDetail() {
  const { serviceId = '' } = useParams()
  const { can } = useEvaluation()
  const editable = can(SERVICE, 'can_update')
  const [savedImage, setSavedImage] = useState<string | null>(null)

  const [service, setService] = useState<Service | null>(null)
  const [category, setCategory] = useState<ServiceCategory | null>(null)
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [order, setOrder] = useState('')
  const [workflow, setWorkflow] = useState<WorkflowSummary | null>(null)
  const [checklist, setChecklist] = useState<Checklist>({ name: null, items: [] })
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [body, vocab] = await Promise.all([services.read(serviceId), services.vocabulary()])
    setService(body.data.service)
    setCategory(body.data.category)
    setOrder(String(body.data.service.display_order))
    setSavedImage(body.data.service.image_url)
    setWorkflow(body.data.workflow)
    setChecklist(body.data.checklist ?? { name: null, items: [] })
    setVocabulary(vocab.data)
    // The categories a service may move into: every one not deleted. A failed
    // read leaves the select holding only the service's own category.
    try {
      setCategories((await services.categories('live')).data)
    } catch {
      setCategories([])
    }
  }, [serviceId])

  useEffect(() => {
    void load().catch(() => setError('That service could not be loaded.'))
  }, [load])

  const reloaded = useCallback(
    async (sentence: string) => {
      setError(null)
      await load()
      setSaved(sentence)
    },
    [load],
  )

  const moves = useCatalogueMoves({
    onMoved: reloaded,
    onError: (sentence) => {
      setSaved(null)
      setError(sentence)
    },
  })

  const saveProfile = async (changes: Partial<Service>) => {
    setError(null)
    try {
      const body = await services.update(serviceId, changes)
      setService((current) => ({ ...body.data, plans: current?.plans, offered: current?.offered }))
      setSaved('Saved.')
      // A move between categories changes what the header and `offered` say.
      if ('category_id' in changes) await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That change could not be saved.')
    }
  }

  const saveChecklist = async (items: ChecklistItem[]) => {
    setError(null)
    try {
      const body = await services.saveChecklist(serviceId, { name: checklist.name, items })
      setChecklist(body.data)
      setSaved('Saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That checklist could not be saved.')
    }
  }

  // The service's own category is always in the list, deleted or not, so
  // the select never shows a value it does not hold.
  const choices = useMemo(() => {
    const live = categories.filter((c) => c.status !== 'deleted')
    return category && !live.some((c) => c.id === category.id) ? [category, ...live] : live
  }, [categories, category])

  const back = { to: '/services', label: 'Services' }

  if (!service) {
    return <RecordPage back={back} title="Service" loading error={error} />
  }

  const published = workflow?.versions.find((v) => v.status === 'published')
  const plans = (service.plans ?? []).filter((plan) => plan.status !== 'deleted')
  const target = { kind: 'service' as const, id: service.id, name: service.name, status: service.status }
  const orderOk = order.trim() === '' || orderValue(order) !== undefined

  return (
    <>
      <RecordPage
        back={back}
        title={service.name}
        status={{ kind: 'catalogue', status: service.status }}
        facts={[
          { label: 'Category', value: category?.name ?? '—' },
          { label: 'For', value: appliesToLabel(service.applies_to) },
          { label: 'Repeats', value: service.is_recurring ? 'Yes' : 'No' },
          { label: 'Plans', value: plans.length },
          {
            label: 'Workflow',
            value: published ? `version ${published.version} live` : 'not published',
          },
        ]}
        primary={
          workflow ? (
            <Link to={`/services/${serviceId}/workflow`} className={buttonClass()}>
              <WorkflowIcon size={16} aria-hidden /> Open the workflow
            </Link>
          ) : undefined
        }
        more={
          editable
            ? actionsFor(service.status).map((action) => ({
                id: action,
                label: MOVE_LABEL[action],
                tone: action === 'delete' ? ('danger' as const) : ('default' as const),
                separatorBefore: action === 'delete',
                onSelect: () => moves.request(target, action),
              }))
            : undefined
        }
      >
        {error && <Alert tone="error">{error}</Alert>}
        {saved && !error && <Alert tone="success">{saved}</Alert>}
        {service.status === 'deleted' && (
          <Alert tone="warning">
            This service is deleted: it is in no list but <strong>Deleted</strong>, and nothing new can be opened on it.
            The cases, invoices and bookings that name it keep it. Restore it from <em>More</em> — it comes back
            inactive.
          </Alert>
        )}
        {service.status === 'active' && service.offered === false && (
          <Alert tone="info">
            This service is active, but its category{category ? ` “${category.name}”` : ''} is not, so it is not offered
            anywhere new. Activate the category on the Services page to offer it.
          </Alert>
        )}

        <RecordSection
          id="service-details"
          icon={<Settings2 size={18} aria-hidden />}
          title="Details"
          description={editable ? 'Each field saves as you leave it.' : 'You can read this service, not change it.'}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              value={service.name}
              disabled={!editable}
              onChange={(e) => setService({ ...service, name: e.target.value })}
              onBlur={() => saveProfile({ name: service.name })}
            />
            <Input
              label="SAC code"
              value={service.sac_code ?? ''}
              disabled={!editable}
              placeholder="Your firm's classification for this service"
              hint="ProDesk ships no SAC code on any template. It goes on your own invoices under your own GSTIN, so it is yours to set."
              onChange={(e) => setService({ ...service, sac_code: e.target.value })}
              onBlur={() => saveProfile({ sac_code: service.sac_code })}
            />
            <Select
              label="Category"
              value={service.category_id}
              disabled={!editable}
              hint="Moving it changes where it sits on your website and in every list."
              onChange={(e) => {
                setService({ ...service, category_id: e.target.value })
                void saveProfile({ category_id: e.target.value })
              }}
            >
              {choices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.status === 'active' ? '' : ` (${c.status})`}
                </option>
              ))}
            </Select>
            <Input
              label="Order"
              inputMode="numeric"
              value={order}
              disabled={!editable}
              error={orderOk ? null : 'A whole number — lower comes first.'}
              hint={orderOk ? 'Lower comes first, on your website and in every list.' : undefined}
              onChange={(e) => setOrder(e.target.value)}
              onBlur={() => {
                const value = orderValue(order)
                if (value !== undefined && value !== service.display_order) void saveProfile({ display_order: value })
              }}
            />
            <div className="sm:col-span-2">
              <Textarea
                label="Description"
                rows={3}
                maxLength={2000}
                value={service.description ?? ''}
                disabled={!editable}
                hint="What the service is, in a sentence or two — drawn on your website where a theme shows it."
                onChange={(e) => setService({ ...service, description: e.target.value })}
                onBlur={() => saveProfile({ description: service.description?.trim() || null })}
              />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {/* FR-I13 — the picture the website's services section shows for
                this service, on every template. Saved by its own button: the
                field reports every keystroke of a pasted URL, and a save per
                keystroke would write half an address. */}
            <ImageField
              label="Picture on your website"
              hint="Shown on this service's card in your website's services section."
              value={service.image_url ?? ''}
              disabled={!editable}
              onChange={(image_url) => setService({ ...service, image_url })}
            />
            {editable && (service.image_url ?? '') !== (savedImage ?? '') && (
              <Button
                size="sm"
                onClick={() => {
                  const next = service.image_url?.trim() || null
                  void saveProfile({ image_url: next }).then(() => setSavedImage(next))
                }}
              >
                Save picture
              </Button>
            )}
            {vocabulary?.icons && (
              <Field
                label="Icon on your website"
                hint="Optional. Drawn beside this service in your website's services section. Saved as soon as you pick it."
              >
                {(control) => (
                  <IconPicker
                    id={control.id}
                    aria-describedby={control['aria-describedby']}
                    value={service.icon}
                    disabled={!editable}
                    icons={vocabulary.icons?.icons ?? []}
                    groups={vocabulary.icons?.groups ?? []}
                    onChange={(icon) => {
                      setService({ ...service, icon })
                      void saveProfile({ icon })
                    }}
                  />
                )}
              </Field>
            )}
          </div>
          <div className="border-border mt-5 flex flex-col gap-3 border-t pt-4">
            <Switch
              checked={service.site_visible}
              disabled={!editable}
              label="Show this service on our website"
              onChange={(e) => {
                setService({ ...service, site_visible: e.target.checked })
                void saveProfile({ site_visible: e.target.checked })
              }}
            />
            <Switch
              checked={service.is_recurring}
              disabled={!editable}
              label="This service repeats"
              onChange={(e) => {
                setService({ ...service, is_recurring: e.target.checked })
                void saveProfile({ is_recurring: e.target.checked })
              }}
            />
            <Switch
              checked={Boolean(service.reverse_charge)}
              disabled={!editable}
              label="Reverse charge applies by default"
              onChange={(e) => {
                setService({ ...service, reverse_charge: e.target.checked })
                void saveProfile({ reverse_charge: e.target.checked })
              }}
            />
            <p className="text-muted-foreground -mt-1 text-xs">
              An invoice priced from this service starts with &ldquo;Tax payable on reverse
              charge&rdquo; on. You can still change it on each invoice.
            </p>
          </div>
        </RecordSection>

        <ServicePlans
          service={service}
          editable={editable}
          vocabulary={vocabulary}
          moves={moves}
          onSaved={reloaded}
          onError={(sentence) => {
            setSaved(null)
            setError(sentence)
          }}
        />

        <RecordSection
          id="service-checklist"
          icon={<ListChecks size={18} aria-hidden />}
          title="Documents we ask for"
          description="The portal asks the client for exactly these, and a case tracks which arrived."
        >
          <div className="space-y-3">
            {checklist.items.length === 0 && (
              <p className="text-muted-foreground text-sm">Nothing asked for yet.</p>
            )}
            {checklist.items.map((item, index) => (
              <div key={index} className="flex flex-wrap items-end gap-3">
                <div className="min-w-0 flex-1 sm:max-w-md">
                  <Input
                    label={index === 0 ? 'Item' : undefined}
                    aria-label={index === 0 ? undefined : `Item ${index + 1}`}
                    value={item.name}
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...checklist.items]
                      next[index] = { ...item, name: e.target.value }
                      setChecklist({ ...checklist, items: next })
                    }}
                  />
                </div>
                <div className="pb-2">
                  <Checkbox
                    checked={item.is_required}
                    disabled={!editable}
                    label="required"
                    onChange={(e) => {
                      const next = [...checklist.items]
                      next[index] = { ...item, is_required: e.target.checked }
                      setChecklist({ ...checklist, items: next })
                    }}
                  />
                </div>
                {editable && (
                  <Button
                    variant="ghost"
                    aria-label={`Remove ${item.name || 'this item'}`}
                    onClick={() => void saveChecklist(checklist.items.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={16} aria-hidden />
                  </Button>
                )}
              </div>
            ))}
            {editable && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() =>
                    setChecklist({
                      ...checklist,
                      items: [
                        ...checklist.items,
                        { name: '', is_required: true, accepted_formats: [], help_text: null },
                      ],
                    })
                  }
                >
                  <Plus size={16} aria-hidden /> Add an item
                </Button>
                <Button onClick={() => void saveChecklist(checklist.items)}>Save checklist</Button>
              </div>
            )}
          </div>
        </RecordSection>

        <RecordSection
          id="service-workflow"
          icon={<FileText size={18} aria-hidden />}
          title="Workflow"
          description="How a case on this service moves — the stages, who may move it, what the client is told, and what happens automatically."
        >
          {published ? (
            <p className="text-foreground text-sm">
              Version {published.version} is live
              {published.in_use ? ' and cases are running on it' : ''}.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">No version is live yet.</p>
          )}
          {workflow && (
            <Link
              to={`/services/${serviceId}/workflow`}
              className="text-primary mt-2 inline-flex items-center gap-2 text-sm font-medium hover:underline"
            >
              <WorkflowIcon size={16} aria-hidden /> Open the workflow editor
            </Link>
          )}
        </RecordSection>
      </RecordPage>

      {moves.dialog}
    </>
  )
}
